import type { DocumentMeta, Rect } from '../core/types';
import type { RenderTarget } from '../gl/renderTarget';
import type { Engine } from './engine';
import {
  channelOp,
  layerMaskOp,
  pixelOp,
  selectionOp,
  type EngineHistoryOp,
  type FullSnapshot,
} from './historyOps';

/** `uMode` values of `combine.frag`. */
const MODE_REPLACE = 0;
const MODE_INTERSECT = 3;
const MODE_INVERT = 4;

/** Scratch slot for single-channel passes that read and write the same target. */
const MASK_SCRATCH = 4;

function fullRect(doc: DocumentMeta): Rect {
  return { x: 0, y: 0, width: doc.width, height: doc.height };
}

function snapshotOf(target: RenderTarget): FullSnapshot {
  return { width: target.width, height: target.height, data: target.texture.readPixels() };
}

/**
 * Runs a single-channel pass through a scratch target and copies the result back,
 * because a pass may not sample the texture it renders into.
 */
function throughScratch(
  engine: Engine,
  doc: DocumentMeta,
  target: RenderTarget,
  draw: (scratch: RenderTarget) => void,
): void {
  const scratch = engine.resourcesFor(doc).r8(MASK_SCRATCH);
  draw(scratch);
  engine.renderer.copy(target.framebuffer, scratch.texture);
}

// --- Layer masks ---

/** Layer - Raster Mask - Invert. */
export function invertLayerMask(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
): EngineHistoryOp[] | null {
  const mask = engine.resourcesFor(doc).layer(layerId)?.mask;
  if (!mask) return null;
  const before = snapshotOf(mask);
  throughScratch(engine, doc, mask, (scratch) => {
    engine.renderer.combineMask(scratch.framebuffer, mask.texture, mask.texture, MODE_INVERT);
  });
  return [layerMaskOp(layerId, before, snapshotOf(mask))];
}

/** Replaces a mask with the current selection, as Photoshop's Add Mask does. */
export function maskFromSelection(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  invert = false,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const selection = resources.selection.activeTexture;
  if (!selection) return null;

  const existing = resources.layer(layerId)?.mask;
  const before = existing ? snapshotOf(existing) : null;
  const mask = resources.ensureMask(layerId);
  throughScratch(engine, doc, mask, (scratch) => {
    engine.renderer.combineMask(
      scratch.framebuffer,
      selection,
      selection,
      invert ? MODE_INVERT : MODE_REPLACE,
    );
  });
  return [layerMaskOp(layerId, before, snapshotOf(mask))];
}

/**
 * Layer - Raster Mask - Apply: bakes the mask into the layer's alpha and drops
 * it. The mask is multiplied into the alpha, which is exactly what the compositor
 * was doing on the fly.
 */
export function applyLayerMask(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const layer = resources.layer(layerId);
  const mask = layer?.mask;
  if (!layer || !mask) return null;

  const rect = fullRect(doc);
  const before = layer.surface.readPixels(rect);
  const maskBefore = snapshotOf(mask);

  const scratch = resources.rgba(1);
  engine.renderer.applyMask(scratch.framebuffer, layer.surface.texture, mask.texture);
  engine.renderer.copy(layer.surface.target.framebuffer, scratch.texture);
  const after = layer.surface.readPixels(rect);

  resources.removeMask(layerId);
  return [
    pixelOp(layerId, { rect, data: before }, { rect, data: after }),
    layerMaskOp(layerId, maskBefore, null),
  ];
}

// --- Alpha channels ---

export function channelFromSelection(
  engine: Engine,
  doc: DocumentMeta,
  channelId: string,
): EngineHistoryOp[] {
  const resources = engine.resourcesFor(doc);
  const existing = resources.channel(channelId);
  const before = existing ? snapshotOf(existing) : null;
  const channel = resources.ensureChannel(channelId);
  const selection = resources.selection.activeTexture;
  if (selection) engine.renderer.copy(channel.framebuffer, selection);
  else channel.clear(0, 0, 0, 0);
  return [channelOp(channelId, before, snapshotOf(channel))];
}

/**
 * Loads a single-channel bitmap into the selection. Photopea's Channels panel
 * calls this "channel to selection"; a layer mask goes the same way.
 */
export function selectionFromCoverage(
  engine: Engine,
  doc: DocumentMeta,
  coverage: RenderTarget | null | undefined,
): EngineHistoryOp[] | null {
  if (!coverage) return null;
  const resources = engine.resourcesFor(doc);
  const before = engine.snapshotSelection(doc);
  resources.selection.restore(coverage.texture.readPixels());
  resources.selection.refresh();
  return [selectionOp(before, engine.snapshotSelection(doc))];
}

/** Intersects a channel with the layer's mask, used when both are shown at once. */
export function combineIntoChannel(
  engine: Engine,
  doc: DocumentMeta,
  channelId: string,
  other: RenderTarget,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const channel = resources.channel(channelId);
  if (!channel) return null;
  const before = snapshotOf(channel);
  throughScratch(engine, doc, channel, (scratch) => {
    engine.renderer.combineMask(
      scratch.framebuffer,
      channel.texture,
      other.texture,
      MODE_INTERSECT,
    );
  });
  return [channelOp(channelId, before, snapshotOf(channel))];
}
